import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, message, Image, Space, Spin, Modal } from 'antd';
import { UserOutlined, LockOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { adminLogin, adminGaLogin, getCaptcha, clearError } from './authSlice';
import CryptoJS from 'crypto-js';

const GA_LENGTH = 6;

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, captcha } = useSelector((state) => state.auth);
  
  const [form] = Form.useForm();
  const [adminCaptchaCode, setAdminCaptchaCode] = useState('');
  const [gaModalVisible, setGaModalVisible] = useState(false);
  const [loginTicket, setLoginTicket] = useState('');
  const [gaCodeArr, setGaCodeArr] = useState(Array(GA_LENGTH).fill(''));
  const [gaSubmitting, setGaSubmitting] = useState(false);
  const gaInputRefs = useRef([]);

  // 密码加密函数
  const encryptPassword = (password) => {
    return CryptoJS.SHA256(password).toString();
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // 组件挂载时自动加载验证码
  useEffect(() => {
    dispatch(getCaptcha());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (captcha.error) {
      message.error(captcha.error);
    }
  }, [captcha.error]);

  // 弹框打开时清空并聚焦第一格
  useEffect(() => {
    if (gaModalVisible) {
      setGaCodeArr(Array(GA_LENGTH).fill(''));
      setTimeout(() => gaInputRefs.current[0]?.focus(), 100);
    }
  }, [gaModalVisible]);

  // 6位填满自动提交
  useEffect(() => {
    if (gaModalVisible && gaCodeArr.every((d) => /^\d$/.test(d)) && !isLoading && !gaSubmitting) {
      handleGaSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gaCodeArr.join('')]);

  // 管理员登录
  const handleAdminLogin = async (values) => {
    try {
      const result = await dispatch(adminLogin({
        username: values.username,
        password: encryptPassword(values.password),
        captchaId: captcha.captchaId,
        captchaCode: values.captchaCode
      })).unwrap();

      if (result.needGa) {
        setLoginTicket(result.loginTicket);
        setGaModalVisible(true);
        return;
      }

      message.success('登录成功');
      navigate('/dashboard');
    } catch (error) {
      // 错误已在slice中处理
      // 登录失败后刷新验证码
      dispatch(getCaptcha());
      setAdminCaptchaCode('');
    }
  };

  // 单格输入
  const handleGaInputChange = (index, value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 1) {
      // 粘贴场景：分发到各格
      const next = Array(GA_LENGTH).fill('');
      digits.split('').forEach((ch, i) => {
        if (i < GA_LENGTH) next[i] = ch;
      });
      setGaCodeArr(next);
      gaInputRefs.current[Math.min(digits.length, GA_LENGTH) - 1]?.focus();
      return;
    }
    setGaCodeArr((prev) => {
      const next = [...prev];
      next[index] = digits;
      return next;
    });
    if (digits && index < GA_LENGTH - 1) {
      gaInputRefs.current[index + 1]?.focus();
    }
  };

  // 退格回退
  const handleGaKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !gaCodeArr[index] && index > 0) {
      e.preventDefault();
      setGaCodeArr((prev) => {
        const next = [...prev];
        next[index - 1] = '';
        return next;
      });
      gaInputRefs.current[index - 1]?.focus();
    }
  };

  // 粘贴整串
  const handleGaPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const digits = text.replace(/\D/g, '');
    if (digits) {
      handleGaInputChange(0, digits);
    }
  };

  // 提交二次验证
  const handleGaSubmit = async () => {
    const gaCode = gaCodeArr.join('');
    if (!/^\d{6}$/.test(gaCode)) {
      message.warning('请输入完整的6位动态验证码');
      return;
    }
    setGaSubmitting(true);
    try {
      await dispatch(adminGaLogin({
        loginTicket,
        gaCode
      })).unwrap();
      message.success('登录成功');
      setGaModalVisible(false);
      navigate('/dashboard');
    } catch (error) {
      // 错误已在slice中处理
      setGaCodeArr(Array(GA_LENGTH).fill(''));
      setTimeout(() => gaInputRefs.current[0]?.focus(), 100);
    } finally {
      setGaSubmitting(false);
    }
  };

  const handleCloseGaModal = () => {
    setGaModalVisible(false);
    setLoginTicket('');
    setGaCodeArr(Array(GA_LENGTH).fill(''));
    form.resetFields();
  };

  // 获取图形验证码
  const handleGetCaptcha = async () => {
    try {
      await dispatch(getCaptcha()).unwrap();
      message.success('验证码已刷新');
      setAdminCaptchaCode('');
    } catch (error) {
      message.error(error || '获取验证码失败');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px'
        }}
        bodyStyle={{ padding: '40px 32px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: '600', 
            color: '#262626',
            marginBottom: '8px'
          }}>
            金融后台管理系统
          </h1>
          <p style={{ color: '#8c8c8c', fontSize: '14px' }}>
            管理员登录
          </p>
        </div>

        <Form
          form={form}
          name="adminLogin"
          onFinish={handleAdminLogin}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入管理员用户名' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="管理员用户名"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6位' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                  图形验证码
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Form.Item
                    name="captchaCode"
                    style={{ flex: 1, margin: 0 }}
                    rules={[
                      { required: true, message: '请输入验证码' },
                      { len: 4, message: '验证码为4位数字' }
                    ]}
                  >
                    <Input
                      placeholder="请输入图形验证码"
                      value={adminCaptchaCode}
                      onChange={(e) => setAdminCaptchaCode(e.target.value.toUpperCase())}
                      maxLength={4}
                      style={{ borderRadius: '8px' }}
                    />
                  </Form.Item>
                  <Button
                    loading={captcha.isLoading}
                    onClick={handleGetCaptcha}
                    icon={<ReloadOutlined />}
                    style={{ borderRadius: '8px' }}
                  >
                    刷新
                  </Button>
                </div>
              </div>
              {captcha.captchaImage && (
                <div style={{ textAlign: 'center', border: '1px solid #ddd', borderRadius: '8px', padding: '8px' }}>
                  <Spin spinning={captcha.isLoading}>
                    <Image
                      src={captcha.captchaImage}
                      alt="图形验证码"
                      style={{ maxWidth: '100%', height: 'auto', cursor: 'pointer' }}
                      preview={false}
                      onClick={handleGetCaptcha}
                    />
                  </Spin>
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#999' }}>
                    点击图片可以刷新
                  </p>
                </div>
              )}
            </Space>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              block
              style={{
                height: '48px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              登录
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: '24px', color: '#8c8c8c', fontSize: '12px' }}>
          <p>© 2024 金融后台管理系统. All rights reserved.</p>
        </div>
      </Card>

      <Modal
        title={null}
        open={gaModalVisible}
        onCancel={handleCloseGaModal}
        footer={null}
        width={420}
        closable={false}
        maskClosable={false}
      >
        <div style={{ textAlign: 'center', padding: '24px 8px 8px' }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            margin: '0 auto 16px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SafetyCertificateOutlined style={{ fontSize: 30, color: '#fff' }} />
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: '#262626', marginBottom: 8 }}>
            二次验证
          </h2>
          <p style={{ color: '#8c8c8c', fontSize: 13, marginBottom: 24 }}>
            该管理员已开启 Google 验证器，请输入 6 位动态验证码
          </p>

          <div
            onPaste={handleGaPaste}
            style={{ display: 'flex', gap: 10, justifyContent: 'center' }}
          >
            {gaCodeArr.map((digit, i) => (
              <Input
                key={i}
                ref={(el) => (gaInputRefs.current[i] = el)}
                value={digit}
                maxLength={6}
                inputMode="numeric"
                onChange={(e) => handleGaInputChange(i, e.target.value)}
                onKeyDown={(e) => handleGaKeyDown(i, e)}
                style={{
                  width: 46,
                  height: 54,
                  textAlign: 'center',
                  fontSize: 22,
                  fontWeight: 600,
                  borderRadius: 10,
                  borderColor: digit ? '#764ba2' : '#d9d9d9',
                  boxShadow: digit ? '0 0 0 2px rgba(118, 75, 162, 0.15)' : 'none',
                  transition: 'all 0.2s'
                }}
              />
            ))}
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 28 }}>
            <Button
              size="large"
              onClick={handleCloseGaModal}
              style={{ borderRadius: 8, minWidth: 110 }}
            >
              取消
            </Button>
            <Button
              type="primary"
              size="large"
              loading={gaSubmitting || isLoading}
              onClick={handleGaSubmit}
              style={{
                borderRadius: 8,
                minWidth: 110,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none'
              }}
            >
              确认
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Login;
